/**
 * WiZi Quiz API Client
 * Handles all API calls for WiZi Quiz functionality
 * Following the existing apiClient.js pattern
 */

import * as http from '../components/http.js';
import { API_BASE_URL } from '../components/apiConfig.js';

/**
 * Get all active/published WiZi quizzes
 */
export const getActiveWiziQuizzes = async () => {
  const api = `${API_BASE_URL}/wizi_quiz/active`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get Active WiZi Quizzes API Error:', error);
    throw error;
  });
};

/**
 * Get quiz details by quiz ID
 */
export const getWiziQuizDetails = async (wiziQuizId: number) => {
  const api = `${API_BASE_URL}/wizi_quiz/${wiziQuizId}`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get WiZi Quiz Details API Error:', error);
    throw error;
  });
};

/**
 * Start a new quiz attempt
 */
export const startWiziQuizAttempt = async (wiziQuizId: number) => {
  const api = `${API_BASE_URL}wizi_quiz/start_mock_test/${wiziQuizId}`;
  return http.post(api, {} as any).then(response => {
    return response;
  }).catch(error => {
    console.error('Start WiZi Quiz Attempt API Error:', error);
    throw error;
  });
};

/**
 * Get questions for a specific attempt
 */
export const getWiziQuizAttemptQuestions = async (attemptId: number) => {
  const api = `${API_BASE_URL}/wizi_quiz/attempt_questions/${attemptId}`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get WiZi Quiz Attempt Questions API Error:', error);
    throw error;
  });
};

/**
 * Submit answer for a question
 */
export const submitWiziQuizAnswer = async (data: {
  question_user_id: number;
  user_answer: string;
  time_spent: number;
}) => {
  const api = `${API_BASE_URL}/wizi_quiz/submit_answer`;
  return http.post(api, data as any).then(response => {
    return response;
  }).catch(error => {
    console.error('Submit WiZi Quiz Answer API Error:', error);
    throw error;
  });
};

/**
 * Update question status (mark for review, clear response)
 */
export const updateWiziQuizQuestionStatus = async (data: {
  question_user_id: number;
  status: 'not_attempted' | 'marked_review' | 'answered_marked_review';
}) => {
  const api = `${API_BASE_URL}/wizi_quiz/update_question_status`;
  return http.post(api, data as any).then(response => {
    return response;
  }).catch(error => {
    console.error('Update WiZi Quiz Question Status API Error:', error);
    throw error;
  });
};

/**
 * Update current question for resume functionality
 */
export const updateWiziQuizCurrentQuestion = async (data: {
  attempt_id: number;
  current_question_index: number;
}) => {
  const api = `${API_BASE_URL}/wizi_quiz/update_current_question/${data.attempt_id}`;
  return http.post(api, { current_question_index: data.current_question_index } as any).then(response => {
    return response;
  }).catch(error => {
    console.error('Update WiZi Quiz Current Question API Error:', error);
    throw error;
  });
};

/**
 * Alias for updateWiziQuizCurrentQuestion for backward compatibility
 */
export const updateCurrentQuestion = updateWiziQuizCurrentQuestion;

/**
 * Complete quiz attempt
 */
export const completeWiziQuizAttempt = async (attemptId: number) => {
  const api = `${API_BASE_URL}/wizi_quiz/complete_attempt/${attemptId}`;
  return http.post(api, {} as any).then(response => {
    return response;
  }).catch(error => {
    console.error('Complete WiZi Quiz Attempt API Error:', error);
    throw error;
  });
};

/**
 * Get user's attempts for a specific quiz
 */
export const getMyWiziQuizAttempts = async (wiziQuizId: number) => {
  const api = `${API_BASE_URL}/wizi_quiz/my_attempts/${wiziQuizId}`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get My WiZi Quiz Attempts API Error:', error);
    throw error;
  });
};

/**
 * Get detailed results for a completed attempt
 */
export const getWiziQuizAttemptResult = async (attemptId: number) => {
  const api = `${API_BASE_URL}/wizi_quiz/attempt_result/${attemptId}`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get WiZi Quiz Attempt Result API Error:', error);
    throw error;
  });
};

/**
 * Get leaderboard for a quiz
 */
export const getWiziQuizLeaderboard = async (wiziQuizId: number, limit: number = 50) => {
  const api = `${API_BASE_URL}/wizi_quiz/leaderboard/${wiziQuizId}?limit=${limit}`;
  return http.get(api).then(response => {
    return response;
  }).catch(error => {
    console.error('Get WiZi Quiz Leaderboard API Error:', error);
    throw error;
  });
};

/**
 * Helper function to check if response is successful
 */
export const isWiziQuizSuccess = (response: any): boolean => {
  return response && (response.status === 'success' || response.status === true);
};

/**
 * Helper function to get error message
 */
export const getWiziQuizErrorMessage = (response: any): string => {
  return response?.message || 'An error occurred';
};
